/**
 * React Query Context Provider
 *
 * Provides global query caching and state management for server data.
 * Reduces Firestore reads by caching responses and managing background refetches.
 *
 * Benefits:
 * - Automatic caching (5 min default)
 * - Background refetching
 * - Request deduplication
 * - Optimistic updates
 * - Reduced Firestore costs
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode } from 'react';

// Create a client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 1000 * 60 * 5,

      // Keep unused data in cache for 10 minutes
      gcTime: 1000 * 60 * 10,

      // Retry failed requests
      retry: 1,

      // Refetch on window focus (configurable per query)
      refetchOnWindowFocus: false,

      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

export function QueryProvider({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

// Export for use in components that need to invalidate queries
export { queryClient };
